/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import SlickGrid1, { typedColumns } from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { excludedUsersUrl, portalUsersUrl } from "@Services/dataManager";
import AddRule from "./AddRule";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { addUserToProject } from "@Actions/projectUsers";
import { getProjectRoles } from "@Actions/projectSetting";
import { getAllPositionUsersData } from "@Actions/orgStructure";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import _ from "lodash";
import type { ProjectRoleItem } from "@ServerTypes/projects/project.schema";
import type { ExcludedUsersControllerRequest } from "@ServerTypes/accounts/accounts.schema";
import type { GetPortalUsersRequest } from "@ServerTypes/portal/portal.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";

/**
 * A row of the user-picker grid (`excludedUsersUrl` when adding to a Project, `portalUsersUrl`
 * otherwise). Declared here rather than derived: the two endpoints share this projection but neither
 * response schema models it (`projectUserItemSchema` covers only id/name/email).
 */
interface PortalUserRow extends GridRowBase {
  id: ObjectIdString;
  fullName?: string;
  name?: string;
  userName?: string;
  customUserName?: string;
  designation?: string;
  department?: string;
  created?: number;
  organisationName?: string;
  role?: string;
  email?: string;
  profilePic?: string;
}

/** The picked users grouped by the role id assigned to them — the shape `selectUser` receives. */
type SelectedUsersByRole = Record<string, Array<{ userId: ObjectIdString; fullName?: string; profilePic?: string }>>;

/** A position row from `orgStructure/getAllPositionUsersData`, whose `result` is `z.unknown()`. */
type OrgPositionUser = { heldBy: ObjectIdString; heldByName?: string; heldByProfilePic?: string };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * Narrows the `z.unknown()` `result` of `getAllPositionUsersData` instead of asserting it. The
 * service only ever collects Position records that HAVE a `heldBy` (it filters on it), and a row
 * without a usable one cannot become a picker entry, so such a row is dropped rather than turned
 * into a user with no id.
 */
const toOrgPositionUsers = (result: unknown): OrgPositionUser[] => {
  if (!Array.isArray(result)) return [];
  const rows: unknown[] = result;
  return rows.flatMap((row) => {
    if (!isRecord(row)) return [];
    const heldBy = objectIdString.safeParse(row.heldBy).data;
    if (!heldBy) return [];
    return [
      {
        heldBy,
        heldByName: typeof row.heldByName === "string" ? row.heldByName : undefined,
        heldByProfilePic: typeof row.heldByProfilePic === "string" ? row.heldByProfilePic : undefined,
      },
    ];
  });
};

/** An org-structure node picked in the org-structure tab. */
type OrgStructureNode = { id: ObjectIdString; type?: string; typeId?: string };

type RoleOption = { id?: string; frontendName?: string };

type RoleChangeArgs = { value?: string; itemData: RoleOption | null };

type UserPickerQuery = NonNullable<ExcludedUsersControllerRequest["value"]> &
  NonNullable<GetPortalUsersRequest["value"]>;

function AddUsersToProject({
  projectId,
  closeModal,
  selectUser,
  refreshAlreadyAddedUserData,
  customOnNext,
  customHeader = "Assign Users to Project",
  groupId,
  excludedUsers = [],
  source,
  defaultRoleOptions,
}: Readonly<{
  /** Present on the add-to-Project flow; absent on the add-to-Group and create-Project flows. */
  projectId?: ObjectIdString;
  closeModal: () => void;
  /** Only the create-Project flow supplies this; asserted at its single call site. */
  selectUser?: ((users: SelectedUsersByRole) => void) | null;
  /** Only the add-to-Project flow supplies this; asserted at its single call site. */
  refreshAlreadyAddedUserData?: (() => void) | null;
  /** When supplied, replaces the built-in "assign roles" second step. */
  customOnNext?: ((selectedData: PortalUserRow[]) => void | Promise<void>) | null;
  customHeader?: string;
  groupId?: ObjectIdString | null;
  excludedUsers?: ObjectIdString[];
  source?: "portalSetting" | "projectSetting";
  /**
   * The roles to offer when there is no `projectId` to fetch them for — the create-Project
   * flow's default project roles, already translated by the caller.
   */
  defaultRoleOptions?: readonly RoleOption[];
}>) {
  const { t } = useTranslation();
  const dataGrid = useRef<SlickGrid1Ref<PortalUserRow> | null>(null);
  const [assignRoleModalVisible, setAssignRoleModalVisible] = useState(false);
  const [addUsersModalVisible, setAddUsersModalVisible] = useState(true);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [bulkSelectedRole, setBulkSelectedRole] = useState<string | undefined>("");
  const [selectedRowData, setSelectedRowData] = useState<PortalUserRow[]>([]);
  /** user id → the project-role id picked for that user. */
  const [usersRoles, setUsersRoles] = useState<Record<string, string | undefined>>({});
  const [activeTab] = useState("users");
  const [createRuleToggle] = useState(false);
  /** The row whose profile card is open; `null` while the card is closed. */
  const [selectedUserDataForProfile, setSelectedUserDataForProfile] = useState<PortalUserRow | null>(null);
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [selectedOrgStructure, setSelectedOrgStructure] = useState<OrgStructureNode[]>([]);
  const [orgUsersList, setOrgUsersList] = useState<PortalUserRow[]>([]);
  const [defaultRule, setDefaultRule] = useState("");

  const roleData = useSelector((state: RootState) => state.roleData) as ProjectRoleItem[];

  const roleOptions = useMemo<readonly RoleOption[]>(
    () => (projectId ? roleData : (defaultRoleOptions ?? [])),
    [projectId, roleData, defaultRoleOptions],
  );
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const dispatch = useDispatch();

  const query = useMemo((): UserPickerQuery => {
    if (projectId) {
      return { params: { projectId }, projectId };
    }
    if (groupId) {
      return { params: { resourceType: "RESOURCE_TYPE_GROUP", resourceId: groupId } };
    }
    return { params: { excludedUsers, excludeCurrentUser: true } };
  }, [projectId, groupId, excludedUsers]);

  const backClickHandler = () => {
    handleBackClick();
    setAssignRoleModalVisible((prev) => !prev);
    setBulkSelectedRole("");
    setUsersRoles({});
  };

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectRoles({ projectId }));
    }
  }, [projectId]);

  const AssignRoleSubmitHandler = async () => {
    if (projectId) {
      // `usersRoles` is keyed/valued as plain strings — the picker also serves the create-Project
      // flow, whose default role ids ("1".."3") are not ObjectIds — so both halves are PARSED, not
      // asserted, before going to an endpoint that requires ObjectIds. On this branch the roles come
      // from `getProjectRoles`, so a value that does not parse means the picked role never made it
      // into state; report it instead of posting a body the server will reject.
      const saveData: Array<{
        userId: ObjectIdString;
        roleId: ObjectIdString;
        resourceId: ObjectIdString;
        resourceType: string;
      }> = [];
      for (const [user, role] of Object.entries(usersRoles)) {
        const userId = objectIdString.safeParse(user).data;
        const roleId = objectIdString.safeParse(role).data;
        if (!userId || !roleId) {
          dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
          return;
        }
        saveData.push({ userId, roleId, resourceId: projectId, resourceType: "RESOURCE_TYPE_PROJECT" });
      }
      const res = await addUserToProject({ data: saveData, source });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Selected User(s) have been added to the project successfully!"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage || SOMETHING_WENT_WRONG),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      refreshAlreadyAddedUserData!();
      closeModal();
    } else {
      const userDataWithRole: SelectedUsersByRole = {};
      selectedRowData.forEach((data) => {
        if (Object.hasOwn(userDataWithRole, usersRoles[data.id]!)) {
          userDataWithRole[usersRoles[data.id]!] = [
            ...userDataWithRole[usersRoles[data.id]!]!,
            {
              userId: data.id,
              fullName: data.fullName,
              profilePic: data.profilePic,
            },
          ];
        } else {
          userDataWithRole[usersRoles[data.id]!] = [
            {
              userId: data.id,
              fullName: data.fullName,
              profilePic: data.profilePic,
            },
          ];
        }
      });
      selectUser!(userDataWithRole);
      closeModal();
    }
  };

  const handleCancelAddUsersModal = () => {
    closeModal();
  };

  const handleRowSelectionChange = () => {
    if (dataGrid.current) {
      const selectedItems = dataGrid.current.dataView.getAllSelectedItems();
      if (selectedItems.length) {
        setNextEnabled(true);
      } else {
        setNextEnabled(false);
      }
    }
  };

  useEffect(() => {
    if (selectedOrgStructure.length) {
      setNextEnabled(true);
    } else {
      setNextEnabled(false);
    }
  }, [selectedOrgStructure.length]);

  const getUserAvatar = (id: ObjectIdString, userName?: string, profilePic?: string, avaterOnly?: boolean) => {
    return <UserNameTagComponent id={id} showAvatarOnly={avaterOnly} size="XL" isCircle />;
  };

  const userProfileOpenHandler = (user: PortalUserRow) => {
    setSelectedUserDataForProfile(user);
  };

  const handleCloseProfileModal = () => {
    setSelectedUserDataForProfile(null);
  };

  const userProfileModalHeader = () => <div className="flex w-full justify-end"></div>;

  // The open row is passed in rather than read off state, so the card renders the row it was opened
  // with instead of re-narrowing the nullable state at every field.
  const userProfileModalContent = (user: PortalUserRow) => (
    <div className="flex flex-col items-center gap-2">
      {getUserAvatar(user.id, user.fullName, user.profilePic, true)}
      <div className="text-base font-semibold text-[var(--primary-text)]">{user.fullName}</div>
      {user.designation && <div className="text-sm text-[var(--primary-text)]">{user.designation}</div>}
      <div className="text-sm text-[var(--primary-text)]">{user.userName}</div>
      {user.organisationName && <div className="text-sm text-[var(--primary-text)]">{user.organisationName}</div>}
      <div className="text-sm text-[var(--primary-text)]">{user.email}</div>
    </div>
  );

  const userProfileModal = (user: PortalUserRow) => (
    <ModalStateDialogComponent
      width="250"
      isVisible
      headerTitle={userProfileModalHeader()}
      onCancel={handleCloseProfileModal}
      modelContent={userProfileModalContent(user)}
    />
  );

  const handleNextClick = async () => {
    let modalClose = true;
    if (activeTab === "users") {
      const selectedRecords = dataGrid.current?.dataView.getAllSelectedItems() || [];
      setSelectedRowData(selectedRecords);
      customOnNext ? await customOnNext(selectedRecords) : setAssignRoleModalVisible(true);
    } else if (createRuleToggle) {
      setRuleModalVisible(true);
      const rules: Array<{ field?: string; label?: string; type: string; operator: string; value: ObjectIdString }> =
        [];
      selectedOrgStructure.forEach((ruleData) => {
        rules.push({
          field: ruleData.typeId,
          label: ruleData.type,
          type: "string",
          operator: "equal",
          value: ruleData.id,
        });
      });
      setDefaultRule(
        JSON.stringify({
          condition: "or",
          rules,
        }),
      );
    } else {
      // Redux models `organisation` as optional (the slice's initialState is `{}`, and the id is
      // missing/null on some accounts) while the endpoint requires an ObjectId — so a missing org is
      // reported rather than asserted away and posted as `undefined`.
      const orgId = userDetails.organisation?.id;
      if (!orgId) {
        dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
        return;
      }
      const response = await getAllPositionUsersData({
        data: selectedOrgStructure.map((record) => record.id),
        orgId,
      });
      if (response.data.status) {
        const positionUsers = toOrgPositionUsers(response.data.result);
        if (positionUsers.length) {
          const userList: PortalUserRow[] = positionUsers.map((orgData) => ({
            id: orgData.heldBy,
            fullName: orgData.heldByName,
            profilePic: orgData.heldByProfilePic,
          }));
          setOrgUsersList(userList);
          customOnNext ? customOnNext(userList) : setAssignRoleModalVisible(true);
        } else {
          dispatch(
            showToastNotification({
              type: TOAST_TYPE_ERROR,
              content: t("Please select orgStructure that have minimun one heldBy position"),
            }),
          );
          modalClose = false;
        }
      }
    }
    if (modalClose) {
      setAddUsersModalVisible(false);
    }
  };

  const handleBackClick = () => {
    setRuleModalVisible(false);
    setAddUsersModalVisible(true);
    setSelectedOrgStructure([]);
  };

  const handleBulkRuleChange = (e: RoleChangeArgs) => {
    setBulkSelectedRole(e.value);
  };

  const setRoleForAllUsers = (userData: PortalUserRow[]) => {
    const allSelectedUserRole = { ...usersRoles };
    userData.map((data) => {
      allSelectedUserRole[data.id] = bulkSelectedRole;
    });
    setUsersRoles(allSelectedUserRole);
  };

  const setUserRole = (event: RoleChangeArgs, userId: ObjectIdString) => {
    const allUserRoles = { ...usersRoles };
    allUserRoles[userId] = event.value;
    setUsersRoles(allUserRoles);
  };

  const columnDefinitions = typedColumns<PortalUserRow>([
    {
      id: "customUserName",
      field: "customUserName",
      name: t("Emp ID"),
      minWidth: 100,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "fullName",
      field: "fullName",
      name: t("User Name"),
      minWidth: 150,
      filterType: "Text",
      formatterType: "userTag",
      _valueGetter: (row: PortalUserRow) => row.id,
      // Filter/search/options must work on the visible name, not the id _valueGetter feeds the avatar.
      _filterValueGetter: (row: PortalUserRow) => row.fullName || row.name || "",
      _excelValueGetter: (row: PortalUserRow) => row.fullName || row.name || row.id || "",
    },
    {
      id: "designation",
      field: "designation",
      name: t("Designation"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "department",
      field: "department",
      name: t("Department"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "created",
      field: "created",
      name: t("Date Added"),
      minWidth: 120,
      formatterType: "Date",
      filterType: "Date",
    },
    {
      id: "organisationName",
      field: "organisationName",
      name: t("Org"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "role",
      field: "role",
      name: t("Portal Role"),
      minWidth: 120,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
    {
      id: "email",
      field: "email",
      name: t("Email"),
      minWidth: 150,
      filterType: "Text",
      clipMode: "EllipsisWithTooltip",
    },
  ]);

  const addUserModalHeader = () => (
    <div className="flex w-full justify-between" data-testid="addUserModalHeader">
      <span className="text-[1.75rem] font-bold text-[var(--primary-text)]" data-testid="assignUsersToProject">
        {customHeader}
      </span>
    </div>
  );

  const GridDataList = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        remoteDataSource={projectId ? excludedUsersUrl : portalUsersUrl}
        query={query}
        columnDefinitions={columnDefinitions}
        rowHeight={68}
        gridHeight="100%"
        onSelectedRowIdsChanged={handleRowSelectionChange}
        enableSearchBar
        enableFilteringOption
        enableSortingOption
        enableColumnChooser
        enableRowResize
        infiniteScroll
        data-testid="gridCompAssignUsersToProject"
      />
    ),

    [addUsersModalVisible],
  );

  const addUserModalContent = () => (
    <>
      <div>
        {!customOnNext && (
          <div className="flex justify-between p-[1.375rem]">
            <div className="flex items-center gap-5">
              <div className="flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-full bg-[var(--button-light)]">
                <span className="text-[var(--primary-text)]">1</span>
              </div>
              <div className="text-lg font-semibold text-[var(--primary-text)]" data-testid="adduserModalHeaderText">
                {t("Add users to this project from the list of available users")}
              </div>
            </div>
          </div>
        )}

        <div className="bg-[var(--system-colour-43)]">
          <div className="h-[25rem]">{GridDataList()}</div>
        </div>
      </div>
    </>
  );

  const assignRuleModalHeader = () => (
    <div className="flex w-full justify-between">
      <span className="text-[1.75rem] font-bold text-[var(--primary-text)]" data-testid="assignProjectRoleHeading">
        {t("Assign Project Role to selected users")}
      </span>
    </div>
  );

  const assignRuleModalContent = (userData: PortalUserRow[]) => (
    <div className="flex flex-col gap-5 pb-2">
      <div className="flex items-center gap-5 p-[1.375rem] pb-0 ps-10">
        <div className="flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-full bg-[var(--button-light)]">
          <span className="text-[var(--primary-text)]">2</span>
        </div>
        <div className="text-lg font-semibold text-[var(--primary-text)]" data-testid="selectSingleRoleToAll">
          {t("Select a single role for selected users")}
        </div>
      </div>

      <div className="flex h-20 items-center gap-10 border-px border-[var(--button-light)] ps-10">
        <div className="text-lg font-semibold text-[var(--primary-text)]" data-testid="role">
          {t("Role")}
        </div>
        <AutoComplete
          value={roleOptions.find((opt) => opt.id === bulkSelectedRole) || null}
          options={roleOptions}
          valueField="id"
          getOptionLabel={(option) => option.frontendName || ""}
          onChange={(_event, newValue) => handleBulkRuleChange({ value: newValue?.id, itemData: newValue })}
          renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select Role")} />}
          sx={{ width: "8.75rem" }}
          data-testid="selectRoleInBulkUpdate"
        />
        <ButtonComponent
          buttonType={BUTTON_TYPES.BORDER_BUTTON}
          disabled={!bulkSelectedRole}
          onClick={() => setRoleForAllUsers(userData)}
          data-testid="bulkUpdateBtn"
        >
          {t("Bulk Update")}
        </ButtonComponent>
      </div>
      <div className="ps-10 text-lg font-semibold" data-testid="updateUsersIndividually">
        {t(".. or update the user roles individually")}
      </div>
      <div className="flex w-full flex-col gap-5 ps-10">
        <div className="flex w-full items-center gap-5">
          <span className="flex-1 cursor-pointer truncate text-base font-semibold" data-testid="userText">
            {t("User")}
          </span>
          <span className="flex-1 truncate text-base font-semibold" data-testid="roleText">
            {t("Role")}
          </span>
        </div>
        <div className="flex max-h-[14.375rem] flex-col gap-3 overflow-auto pe-2.5" data-testid="userAssignRoleDiv">
          {userData.map((userData, index: number) => (
            <div key={index} className="flex w-full items-center gap-5">
              <span
                className="flex-1 cursor-pointer truncate text-base font-semibold"
                onClick={() => {
                  userProfileOpenHandler(userData);
                }}
                data-testid="userProfileDetail"
              >
                {userData.fullName && getUserAvatar(userData.id, userData.fullName, userData.profilePic)}
              </span>
              <span className="flex-1 truncate text-base font-semibold" data-testid="selectRoleIndividually">
                <AutoComplete
                  value={roleOptions.find((opt) => opt.id === usersRoles[userData.id]) || null}
                  onChange={(_event, newValue) => setUserRole({ value: newValue?.id, itemData: newValue }, userData.id)}
                  options={roleOptions}
                  valueField="id"
                  getOptionLabel={(option) => option.frontendName || ""}
                  renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select Role")} />}
                />
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const addUserModal = () => (
    <ModalStateDialogComponent
      width="80%"
      allowModalPadding={false}
      headerTitle={addUserModalHeader()}
      modelContent={addUserModalContent()}
      onCancel={handleCancelAddUsersModal}
      footerContent={
        <div className="flex gap-[1.375rem]">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={handleCancelAddUsersModal}
            data-testid="cancelAddUserBtn"
          >
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleNextClick}
            disabled={!nextEnabled}
            data-testid="nextAddUserBtn"
          >
            {" "}
            {t("Next")}{" "}
          </ButtonComponent>
        </div>
      }
      isVisible={addUsersModalVisible}
    />
  );

  const assignRuleModal = (userData: PortalUserRow[]) => (
    <ModalStateDialogComponent
      allowModalPadding={false}
      width="60%"
      isVisible={assignRoleModalVisible}
      onCancel={handleCancelAddUsersModal}
      headerTitle={assignRuleModalHeader()}
      modelContent={assignRuleModalContent(userData)}
      footerContent={
        <div className="flex gap-[1.375rem]">
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={backClickHandler}
            data-testid="backBtnOfAssignRole"
          >
            {" "}
            {t("Back")}{" "}
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={AssignRoleSubmitHandler}
            disabled={Object.keys(usersRoles).length != userData.length}
            data-testid="AddSelectedBtn"
          >
            {" "}
            {t("Add Selected")}{" "}
          </ButtonComponent>
        </div>
      }
    />
  );

  return (
    <div>
      {addUsersModalVisible && addUserModal()}
      {assignRoleModalVisible && assignRuleModal(activeTab === "users" ? selectedRowData : orgUsersList)}
      {selectedUserDataForProfile && userProfileModal(selectedUserDataForProfile)}
      {ruleModalVisible && (
        <AddRule closeModalPopup={closeModal} defaultRule={defaultRule} handleBackClick={handleBackClick} />
      )}
    </div>
  );
}

export default AddUsersToProject;
